import { Box, Button, Divider, Grid, Popover, Typography } from '@mui/material';
import React, { useState, MouseEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import WSTextField from '../TextBox/WSTextBox';
import WSSelectField from '../Select/WSSelect';
import WSDateField from '../DateField/WSDateField';
import WSMultiCheckBox from '../CheckBox/WSMultiCheckBox';
import { IOptions } from '../Lib/Lib';
import WSRadioField from '../Radio/WSRadio';
import { IsSmallScreen } from '../../commonFunctions/CommonFunctions';
import FilterListIcon from '@mui/icons-material/FilterList'
import SaveIcon from '@mui/icons-material/Save'
import ResetIcon from '@mui/icons-material/Restore'

interface IFilterProps {
    rows: IRows[];
    onFilterChange: (filters: any) => void;
    alignment?: 'left' | 'center' | 'right';
    headerType?: "simple" | "HaderTopCornered"
}

export interface IRows {
    type: "text" | "checkbox" | "date" | "dropdown" | "radio";
    label: string;
    fieldName: string;
    options?: IOptions[];
    range?: boolean;
    operator?: string;
    align?: 'horizontal' | 'vertical';
    rowDivision?: 6 | 12;
    showOperator?: boolean;
}

interface IFilter {
    field: string;
    operator: Operator | string;
    value: string | number;
}

export enum Operator {
    EqualTo = 'eq',
    NotEqualTo = 'ne',
    GreaterThan = 'gt',
    LessThan = 'lt',
    GreaterThanOrEqual = 'gte',
    LessThanOrEqual = 'lte',
    In = 'in',
    NotIn = 'nin',
    Like = 'regex',
}


export const WSFilterComponent: React.FC<IFilterProps> = React.memo(({ rows, onFilterChange, alignment = 'left', headerType = "simple" }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const methods = useForm();
    const { register, handleSubmit, control, reset, formState: { } } = methods;
    const isMobile = IsSmallScreen();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        reset();
    };

    const handleReset = () => {

        const emptyValues = rows.reduce((acc, row) => {
            acc[row.fieldName] = ''; // or [] for multi-select/checkbox fields
            return acc;
        }, {} as Record<string, any>);

        // Reset the form with empty values
        reset(emptyValues);

        // Clear filters
        onFilterChange([]);
        reset();
    };

    const open = Boolean(anchorEl);
    const id = open ? 'filter-popover' : undefined;

    const removeEmptyFilters = (filters: any) => {
        return Object.keys(filters).reduce((acc, key) => {
            const value = filters[key];
            if (value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
                acc[key] = value;
            }
            return acc;
        }, {} as any);
    };

    const onSubmit = (data: any) => {
        const cleanedFilters = removeEmptyFilters(data);
        const appliedFilters = rows.reduce((acc, column) => {
            if (cleanedFilters[column.fieldName]) {
                acc.push({
                    field: column.fieldName,
                    operator: column.operator || Operator.EqualTo,
                    value: cleanedFilters[column.fieldName],
                });
            }
            return acc;
        }, [] as IFilter[]);
        onFilterChange(appliedFilters);
        setAnchorEl(null);
    };
    const sxHaderTopCornered: any = {
        backgroundColor: "#A71A68",
        textTransform: "none",
        "&:hover": { backgroundColor: "#b94886" }
    }
    const sxSimple: any = {
        height: 32, width: isMobile ? 20 : undefined,
        backgroundColor: "#A71A68",
        textTransform: "none",
        "&:hover": { backgroundColor: "#b94886" }
    }
    return (
        <>
            <Box
                display="flex"
                justifyContent={alignment}
                sx={{
                    height: isMobile ? 33 : 32,
                    marginTop: isMobile ? -2.2 : undefined
                }}
            >
                <Button
                    aria-describedby={id}
                    onClick={handleClick}
                    startIcon={
                        <FilterListIcon sx={{
                            color: '#4AD16E',
                        }} />
                    }
                    sx={{
                        height: '36px',
                        px: 2,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        background: 'white',
                        border: '2px solid transparent',
                        borderRadius: '8px',
                        backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'padding-box, border-box',
                        color: 'transparent',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            bottom: '10px',
                            left: '8px',
                            zIndex: -1,
                            background: 'linear-gradient(to right, #27d07c, #16c4e4)',
                            borderRadius: '20px',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                        },
                        '&:hover': {
                            '&::before': {
                                opacity: 0.2,
                            },
                            boxShadow: '0 0 8px rgba(39, 208, 124, 0.5)',
                        },
                        '& .MuiButton-startIcon': {
                            marginRight: '6px',
                        },
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            backgroundImage: 'linear-gradient(to right, #27d07c, #16c4e4)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            color: 'transparent',
                        }}
                    >
                        Filter
                    </Box>
                </Button>
            </Box>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                style={{ maxWidth: 1000, marginTop: 5 }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                className="popover-main"
            >
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} className="popover-content">
                        <Grid container alignItems="center" style={{ padding: 12, marginLeft: 10, backgroundColor: '#ECF3F3' }}>
                            <Typography style={{ fontWeight: 'bold', color: '#098963', fontSize: '1rem' }}>
                                Filter Options
                            </Typography>
                        </Grid>
                        <Divider style={{ marginLeft: 10, }} />

                        <Grid item style={{ margin: 10 }}>
                            <Grid container spacing={1}>
                                {rows.map(({ type, fieldName, label, options, align, rowDivision, showOperator }) => (
                                    <Grid item sm={rowDivision ? rowDivision : 12} xs={12} key={fieldName}>
                                        {/* {showOperator && (
                                            <WSSelectField
                                                options={operatorOptions}
                                                id={`${fieldName}_operator`}
                                                name={`${fieldName}_operator`}
                                                control={control}
                                                label="Operator"
                                                defaultValue={Operator.EqualTo}
                                            />
                                        )} */}
                                        {type === 'text' && (
                                            <WSTextField
                                                control={control}
                                                id={fieldName}
                                                {...register(fieldName)}
                                                name={fieldName}
                                                label={label}
                                            />
                                        )}
                                        {type === 'dropdown' && (
                                            <WSSelectField
                                                options={options}
                                                {...register(fieldName)}
                                                id={fieldName}
                                                control={control}
                                                isMobile={isMobile}
                                                label={label}
                                                name={fieldName}
                                            />
                                        )}
                                        {type === 'date' && (
                                            <WSDateField
                                                id={fieldName}
                                                name={fieldName}
                                                label={label}
                                                dateFormat="D/MM/YYYY"
                                                control={control}
                                                defaultValue={null}
                                                rules={{ required: false }}
                                            />
                                        )}
                                        {type === 'checkbox' && (
                                            <WSMultiCheckBox
                                                id={fieldName}
                                                label={label}
                                                name={fieldName}
                                                options={options || []}
                                                align={align}
                                                defaultValue={[]}
                                                rules={{ required: false }}
                                            />
                                        )}
                                        {type === 'radio' && (
                                            <WSRadioField
                                                id={fieldName}
                                                label={label}
                                                name={fieldName}
                                                align='Horizontal'
                                                options={options || []}
                                                defaultValue={[]}
                                                rules={{ required: false }}
                                                control={control}
                                            />
                                        )}
                                    </Grid>
                                ))}
                            </Grid>

                            <Box>
                                <br />
                                <Divider />
                            </Box>

                            <Grid container justifyContent="flex-end" spacing={1} style={{ paddingTop: 20 }}>
                                <Grid item sm={2} xs={6} style={{ marginRight: !isMobile ? 20 : undefined }} >
                                    <Button
                                        type='submit'
                                        aria-describedby={id}
                                        startIcon={
                                            <SaveIcon sx={{
                                                color: '#4AD16E',
                                            }} />
                                        }
                                        sx={{
                                            height: '36px',
                                            px: 2,
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            background: 'white',
                                            border: '2px solid transparent',
                                            borderRadius: '8px',
                                            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
                                            backgroundOrigin: 'border-box',
                                            backgroundClip: 'padding-box, border-box',
                                            color: 'transparent',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            marginRight: 2,
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                bottom: '10px',
                                                left: '8px',
                                                zIndex: -1,
                                                background: 'linear-gradient(to right, #27d07c, #16c4e4)',
                                                borderRadius: '20px',
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                            },
                                            '&:hover': {
                                                '&::before': {
                                                    opacity: 0.2,
                                                },
                                                boxShadow: '0 0 8px rgba(39, 208, 124, 0.5)',
                                            },
                                            '& .MuiButton-startIcon': {
                                                marginRight: '6px',
                                            },
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                backgroundImage: 'linear-gradient(to right, #27d07c, #16c4e4)',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                color: 'transparent',
                                            }}
                                        >
                                            Apply
                                        </Box>
                                    </Button>
                                </Grid>
                                <Grid item sm={2} xs={6} >
                                    <Button
                                        aria-describedby={id}
                                        onClick={handleReset}
                                        startIcon={
                                            <ResetIcon sx={{
                                                color: '#4AD16E',
                                            }} />
                                        }
                                        sx={{
                                            height: '36px',
                                            px: 2,
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            background: 'white',
                                            border: '2px solid transparent',
                                            borderRadius: '8px',
                                            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #27d07c, #16c4e4)',
                                            backgroundOrigin: 'border-box',
                                            backgroundClip: 'padding-box, border-box',
                                            color: 'transparent',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                bottom: '10px',
                                                left: '8px',
                                                zIndex: -1,
                                                background: 'linear-gradient(to right, #27d07c, #16c4e4)',
                                                borderRadius: '20px',
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                            },
                                            '&:hover': {
                                                '&::before': {
                                                    opacity: 0.2,
                                                },
                                                boxShadow: '0 0 8px rgba(39, 208, 124, 0.5)',
                                            },
                                            '& .MuiButton-startIcon': {
                                                marginRight: '6px',
                                            },
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                backgroundImage: 'linear-gradient(to right, #27d07c, #16c4e4)',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                color: 'transparent',
                                            }}
                                        >
                                            Reset
                                        </Box>
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
            </Popover>
        </>
    );
});
